'use client';

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Box, Typography, TextField, IconButton, Fade, CircularProgress, InputAdornment, useTheme } from '@mui/material';
import {
  Close,
  ArrowUpward,
  ArrowDownward,
  CheckCircle,
  ErrorOutline,
  Stop,
  Add,
  AttachFile,
  InsertDriveFile,
  Download,
  IosShareRounded,
  OpenInNew,
  LocalOfferOutlined,
  SendOutlined,
  DescriptionOutlined,
  LocalShippingOutlined,
  ChatBubbleOutlineRounded,
  ForumOutlined,
} from '@mui/icons-material';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSnackbarContext } from '@/components/SnackbarProvider';
import { useChatOpen } from '@/components/ChatContext';
import { filterSlashCommands, highlightedSlashCommands, parseSlashCommand, type SlashCommand } from '@/components/chat-slash-commands';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { downloadPresignedPdf } from '@/lib/purchaseInvoices/downloadPresignedPdf';
import { shareFile } from '@/lib/app/shareFile';
import { useCanShareFiles } from '@/components/ShareFileButton';
import { useFileViewer } from '@/components/FileViewer';
import { armFillingClipboard, downloadFillingImages, extractWtbClipboardText, startFillingImageDownload } from '@/lib/orders/fillingWhatsAppClient';
import { get, post, request } from '@/lib/fetch';
import { labelLinkOf, type LabelLink, transcriptToDisplay } from '@/lib/app/userChat';
import { errorText } from '@/lib/errorText';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_FILE_TYPES = 'image/*,.pdf,.csv,.xlsx,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function timeGreeting(date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function isLabelPdfUrl(href?: string): boolean {
  if (!href) return false;
  try {
    const parsed = new URL(href, 'http://localhost');
    return parsed.pathname.includes('/labels/') && parsed.pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
}

/** File name from an older chat's signed label link, which carried it in response-content-disposition. */
function labelPdfName(href: string): string {
  try {
    const disposition = new URL(href).searchParams.get('response-content-disposition') ?? '';
    return disposition.match(/filename="([^"]+)"/i)?.[1] || 'Label.pdf';
  } catch {
    return 'Label.pdf';
  }
}

function extractLabelPdfUrls(content: string): string[] {
  const fromMarkdown = [...content.matchAll(/\[(?:[^\]]*)\]\((https?:\/\/[^)\s]+)\)/g)].map((m) => m[1]);
  const bare = [...content.matchAll(/https?:\/\/[^\s<>)"']+/g)].map((m) => m[0].replace(/[.,;:]+$/, ''));
  return [...new Set([...fromMarkdown, ...bare].filter(isLabelPdfUrl))];
}

function isFillingImagesUrl(href?: string): boolean {
  if (!href) return false;
  try {
    return new URL(href, 'http://localhost').pathname.includes('/api/orders/outgoing/filling-images');
  } catch {
    return false;
  }
}

function extractFillingImagesUrls(content: string): string[] {
  const fromMarkdown = [...content.matchAll(/\[(?:[^\]]*)\]\(([^)\s]+)\)/g)].map((m) => m[1]);
  const bare = [...content.matchAll(/\/api\/orders\/outgoing\/filling-images[^\s<>)"']*/g)].map((m) => m[0].replace(/[.,;:]+$/, ''));
  return [...new Set([...fromMarkdown, ...bare].filter(isFillingImagesUrl))];
}

const LABEL_ACTION_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.4,
  height: 24,
  px: 0.9,
  border: 'none',
  bgcolor: 'transparent',
  color: 'text.primary',
  fontFamily: 'inherit',
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '-0.01em',
  lineHeight: 1,
  cursor: 'pointer',
  textDecoration: 'none !important',
  whiteSpace: 'nowrap',
  '&:hover': { bgcolor: 'action.hover', textDecoration: 'none !important' },
} as const;

function AssistantMessageContent({
  content,
  labels,
  streaming,
  caretOn,
  showLabelActions,
  onError,
}: {
  content: string;
  labels?: LabelLink[];
  streaming: boolean;
  caretOn: boolean;
  showLabelActions: boolean;
  onError: (message: string) => void;
}) {
  if (content === '' && streaming) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, px: 0.25 }}>
        {[0, 1, 2].map((dot) => (
          <Box
            key={dot}
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: '#999',
              animation: 'chatDot 1.2s ease-in-out infinite',
              animationDelay: `${dot * 0.16}s`,
              '@keyframes chatDot': {
                '0%, 80%, 100%': { opacity: 0.3, transform: 'translateY(0) scale(0.85)' },
                '40%': { opacity: 0.9, transform: 'translateY(-3px) scale(1)' },
              },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'chatDotFade 1.2s ease-in-out infinite',
                animationDelay: `${dot * 0.16}s`,
                '@keyframes chatDotFade': {
                  '0%, 100%': { opacity: 0.3 },
                  '50%': { opacity: 0.9 },
                },
              },
            }}
          />
        ))}
      </Box>
    );
  }

  const fillingImageUrls = extractFillingImagesUrls(content);
  // Older chats only have the signed link Timmy wrote in the text.
  const labelLinks = [...(labels ?? []), ...extractLabelPdfUrls(content).map((url) => ({ url, name: labelPdfName(url) }))].filter((label, i, all) => all.findIndex((other) => other.url === label.url) === i);

  return (
    <>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => {
            if (href && (isLabelPdfUrl(href) || isFillingImagesUrl(href))) return null;
            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            );
          },
          p: ({ children }) => {
            const nodes = React.Children.toArray(children).filter((node) => node != null && node !== '');
            if (nodes.length === 0) return null;
            const text = nodes.map((node) => (typeof node === 'string' ? node : '')).join('');
            const isWtb = text.trimStart().startsWith('WTB ');
            return (
              <p
                style={
                  isWtb
                    ? {
                        fontFamily: '"SF Mono", "Fira Code", "Consolas", ui-monospace, monospace',
                        fontSize: '0.78rem',
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                        letterSpacing: '-0.01em',
                      }
                    : undefined
                }
              >
                {children}
              </p>
            );
          },
        }}
      >
        {streaming && caretOn ? `${content}\u200A▏` : content}
      </ReactMarkdown>
      {showLabelActions ? labelLinks.map((label) => <LabelPdfActions key={label.url} href={label.url} name={label.name} onError={onError} />) : null}
      {fillingImageUrls.length > 0 && fillingImageUrls.map((href) => <FillingImagesDownload key={href} href={href} onError={onError} />)}
    </>
  );
}

function FillingImagesDownload({ href, onError }: { href: string; onError: (message: string) => void }) {
  const [downloading, setDownloading] = React.useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadFillingImages(href);
    } catch {
      onError('Failed to download images');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      component="button"
      type="button"
      disabled={downloading}
      onClick={() => void handleDownload()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        width: '100%',
        height: 32,
        mt: 1.25,
        px: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'action.hover',
        color: 'text.primary',
        fontFamily: 'inherit',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        cursor: downloading ? 'default' : 'pointer',
        '&:hover': downloading ? undefined : { bgcolor: 'action.selected' },
        '&:disabled': { opacity: 0.65 },
      }}
    >
      {downloading ? <CircularProgress size={12} sx={{ color: '#111' }} /> : <Download sx={{ fontSize: 14 }} />}
      {downloading ? 'Downloading…' : 'Download images'}
    </Box>
  );
}

function LabelPdfActions({ href, name, onError }: { href: string; name: string; onError: (message: string) => void }) {
  const canShare = useCanShareFiles();
  const fileViewer = useFileViewer();
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        my: 0.5,
        borderRadius: '7px',
        border: '1px solid rgba(0,0,0,0.1)',
        bgcolor: 'action.hover',
        overflow: 'hidden',
        verticalAlign: 'middle',
      }}
    >
      <Box component="button" type="button" onClick={() => fileViewer.open({ url: href, name, mime: 'application/pdf' })} sx={LABEL_ACTION_SX}>
        <OpenInNew sx={{ fontSize: 11 }} />
        Open
      </Box>
      {fileViewer.viewer}
      <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'rgba(0,0,0,0.1)' }} />
      <Box component="button" type="button" onClick={() => void downloadPresignedPdf(href, name).catch(() => onError('Failed to download label'))} sx={LABEL_ACTION_SX}>
        <Download sx={{ fontSize: 12 }} />
        Download
      </Box>
      {canShare && (
        <>
          <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: 'rgba(0,0,0,0.1)' }} />
          <Box component="button" type="button" onClick={() => void shareFile(href, name).catch(() => onError('Failed to share label'))} sx={LABEL_ACTION_SX}>
            <IosShareRounded sx={{ fontSize: 12 }} />
            Share
          </Box>
        </>
      )}
    </Box>
  );
}

interface Attachment {
  name: string;
  mimeType: string;
  base64: string;
  previewUrl?: string; // object URL for images — valid for the current page session only
}

interface MessageAttachment {
  name: string;
  mimeType: string;
  previewUrl?: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  /** Labels from create_label's result, shown as Open/Download under the reply. */
  labels?: LabelLink[];
  commandName?: string;
  attachments?: MessageAttachment[];
}

const SLASH_FONT = '"SF Mono", "Fira Code", "Consolas", monospace';
const SLASH_COLOR = '#1d4ed8';

const COMMAND_ICONS: Record<string, React.ElementType> = {
  quote: LocalOfferOutlined,
  fill: SendOutlined,
  proforma: DescriptionOutlined,
  label: LocalShippingOutlined,
};

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  if (file.type.startsWith('image/')) return true;
  if (file.type === 'application/pdf') return true;
  if (file.type === 'text/csv' || name.endsWith('.csv')) return true;
  if (file.type.includes('spreadsheetml') || name.endsWith('.xlsx')) return true;
  if (file.type.includes('wordprocessingml') || name.endsWith('.docx')) return true;
  return false;
}

async function fileToAttachment(file: File): Promise<Attachment> {
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
  return { name: file.name, mimeType: file.type, base64, previewUrl };
}

type ChatTab = { key: string; conversationId: string | null; title: string };

function createTabKey() {
  return Math.random().toString(36).slice(2, 10);
}

interface ChatPanelProps {
  // When provided, a close button and mobile swipe-to-dismiss gesture are shown
  // on the header and this callback is invoked when the user dismisses the panel.
  // Omit it for a full-page chat that has nothing to "close".
  onClose?: () => void;
  // When the floating widget hides the panel without unmounting, pass the open
  // flag so we can re-focus the input and snap to the latest message on reopen.
  visible?: boolean;
  onActiveChatChange?: (id: string | null) => void;
  // Fired when a turn is stored, so the chat list can refresh.
  onChatSaved?: () => void;
  // Opens the saved-chat list. Omit it when that list is already on screen.
  onOpenChats?: () => void;
  // Open or focus this saved chat. A new token focuses it again.
  openChat?: { id: string; title: string; token: number } | null;
  // Bump to open an empty tab.
  newTabToken?: number;
}

export default function ChatPanel({ onClose, visible, onActiveChatChange, onChatSaved, onOpenChats, openChat = null, newTabToken = 0 }: ChatPanelProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { showError } = useSnackbarContext();
  const { user } = useCurrentUser();
  const welcomeName = user?.name.split(' ')[0];
  const { presetPrompt, clearPresetPrompt, initialAssistantMessage, clearInitialAssistantMessage } = useChatOpen();
  const [messages, setMessages] = useState<Message[]>([]);
  const [tabs, setTabs] = useState<ChatTab[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [tabsReady, setTabsReady] = useState(false);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<ChatTab[]>([]);
  const activeKeyRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const skipTabLoadRef = useRef<string | null>(null);
  const preserveMessagesRef = useRef(false);
  const openedByUserRef = useRef(false);
  const sendingTabKeyRef = useRef<string | null>(null);
  const pendingTitleIdRef = useRef<string | null>(null);
  const openChatRef = useRef(openChat);
  const onActiveChatChangeRef = useRef(onActiveChatChange);
  const onChatSavedRef = useRef(onChatSaved);
  tabsRef.current = tabs;
  activeKeyRef.current = activeKey;
  openChatRef.current = openChat;
  onActiveChatChangeRef.current = onActiveChatChange;
  onChatSavedRef.current = onChatSaved;
  const [input, setInput] = useState('');
  const [activeCommand, setActiveCommand] = useState<SlashCommand | null>(null);
  const [inputMultiRow, setInputMultiRow] = useState(false);
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [slashHighlight, setSlashHighlight] = useState(0);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [assistantIdle, setAssistantIdle] = useState(true);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [caretOn, setCaretOn] = useState(true);

  interface ToolCallStatus {
    id: string;
    name: string;
    status: 'running' | 'done' | 'error';
  }
  const [toolCalls, setToolCalls] = useState<ToolCallStatus[]>([]);

  const TOOL_LABELS: Record<string, string> = {
    query_database: 'Querying database',
    create_label: 'Creating shipping label',
    list_docusign_templates: 'Listing DocuSign templates',
    send_docusign: 'Sending DocuSign',
    search_quickbooks_customers: 'Searching QuickBooks customers',
    create_quickbooks_customer: 'Creating QuickBooks customer',
    send_proforma_invoice: 'Sending proforma invoice',
    list_need_to_fill_orders: 'Listing need to fill orders',
    send_filling_whatsapp: 'Sending WhatsApp fill posts',
    list_approvals: 'Listing Approvals',
    approve_outgoing_order: 'Updating Approvals',
  };
  const toolLabel = (name: string) => TOOL_LABELS[name] ?? name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const [dragOver, setDragOver] = useState(false);
  // Counter tracks nested dragenter/dragleave so the overlay stays visible
  // while the pointer moves across child elements.
  const dragCounterRef = useRef(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamingRef = useRef(false);

  // Smooth "typewriter" reveal — decouples on-screen text from the bursty
  // network stream so the assistant appears to type at a natural, even pace.
  const targetTextRef = useRef('');
  const revealLenRef = useRef(0);
  const revealRafRef = useRef<number | null>(null);
  const fillCaptureRef = useRef<{ resolve: (text: string) => void; reject: (error: Error) => void } | null>(null);

  // Swipe-to-dismiss state (mobile only, only meaningful when onClose is provided)
  const swipeTouchStartY = useRef(0);
  const swipeDelta = useRef(0);
  const [swipeTranslate, setSwipeTranslate] = useState(0);

  // Follow new content only while the reader is at the bottom. Scrolling up
  // (wheel, touch, keys, scrollbar) releases it; returning to the bottom re-engages it.
  const stickToBottomRef = useRef(true);
  const userScrollAtRef = useRef(0);
  const pointerHeldRef = useRef(false);

  const scrollToBottom = useCallback((instant = false) => {
    stickToBottomRef.current = true;
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'instant' : 'smooth' });
  }, []);

  const followIfStuck = useCallback(() => {
    if (stickToBottomRef.current) scrollToBottom(true);
  }, [scrollToBottom]);

  const markUserScroll = useCallback(() => {
    userScrollAtRef.current = performance.now();
  }, []);

  // Disabling the field (or swapping the send/stop button) blurs it. Put the
  // caret back unless the user has moved focus somewhere else.
  const focusInputIfIdle = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    // Don't steal focus when a foreign modal/dialog is open — its focus trap will win.
    // Ignore dialogs that live inside the Timmy modal itself.
    if (document.querySelector('[role="dialog"]:not([data-timmy-modal] [role="dialog"])')) return;
    const active = document.activeElement;
    if (active === input || input.contains(active)) return;
    if (active && active !== document.body && active !== document.documentElement) return;
    input.focus();
  }, []);

  // Advances the visible text toward the received text a little each frame.
  // Easing on the backlog means big bursts catch up quickly while small ones
  // still read as smooth typing rather than instant snaps.
  const pumpReveal = useCallback(() => {
    const target = targetTextRef.current;
    const shown = revealLenRef.current;
    if (shown >= target.length) {
      revealRafRef.current = null;
      if (!streamingRef.current) setAssistantIdle(true);
      return;
    }
    const remaining = target.length - shown;
    const step = Math.max(2, Math.ceil(remaining / 7));
    const next = Math.min(target.length, shown + step);
    revealLenRef.current = next;
    const text = target.slice(0, next);
    setMessages((prev) => {
      if (prev.length === 0 || prev[prev.length - 1].role !== 'assistant') return prev;
      const updated = [...prev];
      updated[updated.length - 1] = { ...prev[prev.length - 1], content: text };
      return updated;
    });
    revealRafRef.current = requestAnimationFrame(pumpReveal);
  }, []);

  const flushReveal = useCallback(() => {
    if (revealRafRef.current != null) {
      cancelAnimationFrame(revealRafRef.current);
      revealRafRef.current = null;
    }
    revealLenRef.current = targetTextRef.current.length;
  }, []);

  // Auto-clear file errors after 4 s.
  useEffect(() => {
    if (!fileError) return;
    const id = setTimeout(() => setFileError(null), 4000);
    return () => clearTimeout(id);
  }, [fileError]);

  const processFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    const invalid = files.find((f) => !isAcceptedFile(f));
    if (invalid) {
      setFileError(`"${invalid.name}" is not a supported file type.`);
      return;
    }
    const oversized = files.find((f) => f.size > MAX_FILE_SIZE);
    if (oversized) {
      setFileError(`"${oversized.name}" exceeds the 10 MB limit.`);
      return;
    }
    setFileError(null);
    const newAttachments = await Promise.all(files.map(fileToAttachment));
    setAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      await processFiles(files);
    },
    [processFiles]
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => {
      const att = prev[index];
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    // Only show overlay when the dragged payload contains files.
    if (Array.from(e.dataTransfer.items).some((item) => item.kind === 'file')) {
      dragCounterRef.current += 1;
      setDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); // required to allow drop
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setDragOver(false);
      await processFiles(Array.from(e.dataTransfer.files));
    },
    [processFiles]
  );

  // Blink the typing caret while streaming. Decoupled from the reveal loop so
  // it keeps a steady rhythm even while waiting between network chunks.
  useEffect(() => {
    if (!streaming) return;
    setCaretOn(true);
    const id = setInterval(() => setCaretOn((v) => !v), 530);
    return () => clearInterval(id);
  }, [streaming]);

  const slashQuery = activeCommand ? null : (/^\/([A-Za-z0-9_-]*)$/.exec(input)?.[1] ?? null);
  const slashMatches = slashQuery == null ? [] : filterSlashCommands(slashQuery);
  const slashMenuOpen = slashQuery != null && !slashDismissed && slashMatches.length > 0;
  const typingSlash = slashQuery != null && (slashQuery === '' || slashMatches.length > 0);
  const highlightedCommands = highlightedSlashCommands();

  useEffect(() => {
    setSlashDismissed(false);
    setSlashHighlight(0);
  }, [slashQuery]);

  useEffect(() => {
    const field = inputRef.current;
    setInputMultiRow(!!field && field.scrollHeight > 30);
  }, [input, activeCommand]);

  useEffect(
    () => () => {
      if (revealRafRef.current != null) cancelAnimationFrame(revealRafRef.current);
    },
    []
  );

  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      setShowScrollDown(false);
      return;
    }
    setShowScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight >= 60);
  }, [messages, toolCalls]);

  // Focus the input when the panel becomes visible. On the standalone /timmy
  // page `visible` is omitted so this runs once on mount; in the widget it
  // re-runs each time the user reopens a still-mounted conversation.
  useEffect(() => {
    if (visible === false) return;
    const id = setTimeout(() => {
      // Skip auto-focus when a foreign modal/dialog is open — its focus trap will win.
      // Ignore dialogs that live inside the Timmy modal itself.
      if (!document.querySelector('[role="dialog"]:not([data-timmy-modal] [role="dialog"])')) {
        inputRef.current?.focus();
      }
      scrollToBottom(true);
    }, 200);
    return () => clearTimeout(id);
  }, [visible, scrollToBottom]);

  useEffect(() => {
    if (!presetPrompt) return;
    const parsed = parseSlashCommand(presetPrompt.trim());
    if (parsed) {
      setActiveCommand(parsed.command);
      setInput(presetPrompt.replace(/^\/[A-Za-z0-9_-]+\s*/, ''));
    } else {
      setActiveCommand(null);
      setInput(presetPrompt);
    }
    clearPresetPrompt();
    const id = setTimeout(() => {
      const field = inputRef.current;
      field?.focus();
      const marker = 'Filling from: ';
      const at = field?.value.lastIndexOf(marker) ?? -1;
      const pos = at >= 0 ? at + marker.length : (field?.value.length ?? 0);
      field?.setSelectionRange(pos, pos);
    }, 220);
    return () => clearTimeout(id);
  }, [presetPrompt, clearPresetPrompt]);

  const openNewTab = () => {
    openedByUserRef.current = true;
    const tab: ChatTab = { key: createTabKey(), conversationId: null, title: 'New chat' };
    setTabs((prev) => [...prev, tab]);
    setActiveKey(tab.key);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closeTab = (key: string) => {
    const current = tabsRef.current;
    const index = current.findIndex((tab) => tab.key === key);
    if (index < 0) return;
    const next = current.filter((tab) => tab.key !== key);
    setTabs(next);
    if (key !== activeKeyRef.current) return;
    const neighbor = next[index] ?? next[index - 1];
    setActiveKey(neighbor?.key);
  };

  const selectTab = (key: string) => {
    if (key === activeKeyRef.current) return;
    setActiveKey(key);
  };

  const ensureSendingTab = () => {
    const currentKey = activeKeyRef.current;
    if (currentKey && tabsRef.current.some((tab) => tab.key === currentKey)) {
      sendingTabKeyRef.current = currentKey;
      return;
    }
    const tab: ChatTab = { key: createTabKey(), conversationId: null, title: 'New chat' };
    skipTabLoadRef.current = tab.key;
    activeKeyRef.current = tab.key;
    sendingTabKeyRef.current = tab.key;
    setTabs((prev) => [...prev, tab]);
    setActiveKey(tab.key);
  };

  useEffect(() => {
    if (openedByUserRef.current || tabsRef.current.length > 0 || initialAssistantMessage || newTabToken || openChat?.token) {
      setTabsReady(true);
      return;
    }
    const tab: ChatTab = { key: createTabKey(), conversationId: null, title: 'New chat' };
    setTabs([tab]);
    setActiveKey(tab.key);
    setTabsReady(true);
  }, []);

  useEffect(() => {
    if (!openChat?.token) return;
    openedByUserRef.current = true;
    const existing = tabsRef.current.find((tab) => tab.conversationId === openChat.id);
    if (existing) {
      setActiveKey(existing.key);
      return;
    }
    const tab: ChatTab = { key: createTabKey(), conversationId: openChat.id, title: openChat.title || 'New chat' };
    setTabs((prev) => [...prev, tab]);
    setActiveKey(tab.key);
  }, [openChat]);

  useEffect(() => {
    if (!newTabToken) return;
    openNewTab();
  }, [newTabToken]);

  useEffect(() => {
    if (!initialAssistantMessage || visible === false) return;
    abortRef.current?.abort();
    preserveMessagesRef.current = true;
    openNewTab();
    setMessages([{ role: 'assistant', content: initialAssistantMessage }]);
    clearInitialAssistantMessage();
    setTimeout(() => inputRef.current?.focus(), 220);
  }, [initialAssistantMessage, visible, clearInitialAssistantMessage]);

  useEffect(() => {
    if (!tabsReady) return;
    if (skipTabLoadRef.current && skipTabLoadRef.current === activeKey) {
      skipTabLoadRef.current = null;
      return;
    }
    if (preserveMessagesRef.current) {
      preserveMessagesRef.current = false;
      const kept = tabsRef.current.find((tab) => tab.key === activeKey);
      conversationIdRef.current = kept?.conversationId ?? null;
      onActiveChatChangeRef.current?.(conversationIdRef.current);
      return;
    }

    const tab = tabsRef.current.find((item) => item.key === activeKey);
    const id = tab?.conversationId ?? null;
    conversationIdRef.current = id;
    onActiveChatChangeRef.current?.(id);
    abortRef.current?.abort();
    abortRef.current = null;
    streamingRef.current = false;
    setStreaming(false);
    setToolCalls([]);
    setMessages([]);
    if (!id) {
      setLoadingTranscript(false);
      return;
    }

    let cancelled = false;
    setLoadingTranscript(true);
    get(`/api/chat/conversations/${id}`)
      .then((data) => {
        if (cancelled) return;
        stickToBottomRef.current = true;
        setMessages(transcriptToDisplay(data.messages));
        if (typeof data?.title === 'string' && data.title) {
          setTabs((prev) => prev.map((item) => (item.key === activeKey ? { ...item, title: data.title } : item)));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message = errorText(err, '');
        if (message.startsWith('404') && activeKey) {
          closeTab(activeKey);
          return;
        }
        showError(message || 'Could not open that chat');
      })
      .finally(() => {
        if (!cancelled) setLoadingTranscript(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeKey, tabsReady, showError]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd+T and Ctrl/Cmd+W belong to the browser and cannot be cancelled.
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.code !== 'KeyT' && event.code !== 'KeyW') return;
      if (visible === false) return;
      const root = panelRef.current;
      if (!root?.contains(document.activeElement)) return;
      event.preventDefault();
      if (event.code === 'KeyT') openNewTab();
      else if (activeKeyRef.current) closeTab(activeKeyRef.current);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [visible, activeKey]);

  // When the keyboard opens on iOS, dvh updates but the content doesn't re-scroll.
  // Listen to visualViewport resize and snap messages to the bottom.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || window.innerWidth >= 600) return;
    const onResize = () => setTimeout(followIfStuck, 50);
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [followIfStuck]);

  // Copy WTB lines and save images once the fill reply has fully revealed.
  // The clipboard write is armed in the send click, before the stream, so the
  // browser still treats it as the user's gesture.
  useEffect(() => {
    if (streaming || !assistantIdle || !fillCaptureRef.current) return;
    const pending = fillCaptureRef.current;
    fillCaptureRef.current = null;
    const content = targetTextRef.current;
    const text = extractWtbClipboardText(content);
    const href = extractFillingImagesUrls(content)[0];
    if (text) pending.resolve(text);
    else pending.reject(new Error('Nothing to copy'));
    if (href) startFillingImageDownload(href);
  }, [streaming, assistantIdle]);

  const _shiftFRouter = useRouter();

  // ── Shift+F global shortcut — fire all WTB fills silently ────────────────────
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'KeyF' || !e.shiftKey || e.altKey || e.ctrlKey || e.metaKey || e.repeat) return;
      // Only fire when the chat panel is open — prevents accidental triggers
      // from other pages or when focus is elsewhere.
      if (visible === false) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName?.toLowerCase();
      const editable = (document.activeElement as HTMLElement | null)?.isContentEditable;
      if (tag === 'input' || tag === 'textarea' || editable) return;
      e.preventDefault();
      void (async () => {
        try {
          const data = (await post('/api/orders/outgoing/fill-whatsapp-all', {})) as { messagesText?: string; imageDownloadPath?: string | null } | undefined;
          const text = data?.messagesText?.trim() ?? '';
          if (text) void navigator.clipboard.writeText(text).catch(() => undefined);
          if (data?.imageDownloadPath) void downloadFillingImages(data.imageDownloadPath).catch(() => undefined);
          // Activate the Need to fill filter on the orders page and keep it active
          // until the user manually changes it.
          // If already on /orders, fire the custom event so the filter applies
          // instantly without a navigation. Otherwise navigate to the orders page
          // with the fulfillment param so the mount-time effect picks it up.
          if (window.location.pathname === '/orders') {
            window.dispatchEvent(new CustomEvent('tlc:need-to-fill'));
          } else {
            _shiftFRouter.push('/orders?fulfillment=NEED_TO_FILL');
          }
        } catch {
          // silently ignore
        }
      })();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, _shiftFRouter]);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const byUser = pointerHeldRef.current || performance.now() - userScrollAtRef.current < 300;
    if (distance < 24) stickToBottomRef.current = true;
    else if (byUser) stickToBottomRef.current = false;
    setShowScrollDown(!stickToBottomRef.current && distance >= 60);
  }, []);

  // ── Swipe-to-dismiss (mobile header drag) ────────────────────────────────────
  // No-op unless onClose is provided (nothing to dismiss for a standalone page).

  const handleHeaderTouchStart = (e: React.TouchEvent) => {
    if (!onClose || window.innerWidth >= 600) return;
    swipeTouchStartY.current = e.touches[0].clientY;
    swipeDelta.current = 0;
  };

  const handleHeaderTouchMove = (e: React.TouchEvent) => {
    if (!onClose || window.innerWidth >= 600) return;
    const delta = Math.max(0, e.touches[0].clientY - swipeTouchStartY.current);
    swipeDelta.current = delta;
    setSwipeTranslate(delta);
  };

  const handleHeaderTouchEnd = () => {
    if (!onClose || window.innerWidth >= 600) return;
    if (swipeDelta.current > 80) {
      onClose();
    }
    swipeDelta.current = 0;
    setSwipeTranslate(0);
  };

  // ── Chat send ────────────────────────────────────────────────────────────────

  const handleInputChange = (value: string) => {
    if (!activeCommand && /\s/.test(value)) {
      const promoted = parseSlashCommand(value);
      if (promoted) {
        setActiveCommand(promoted.command);
        setInput(promoted.args);
        return;
      }
    }
    setInput(value);
  };

  const handleSend = async () => {
    const raw = (activeCommand ? `/${activeCommand.name}${input.trim() ? ` ${input.trim()}` : ''}` : input).trim();
    const parsed = raw ? parseSlashCommand(raw) : null;
    if (parsed?.command.requiresArgs && !parsed.args) return;
    const apiText = parsed ? parsed.command.expand(parsed.args) : raw;
    if ((!apiText && attachments.length === 0) || streaming) return;
    ensureSendingTab();
    window.navigator.vibrate?.(1);

    // Capture and clear attachments before the async operation.
    const currentAttachments = attachments;
    setAttachments([]);

    const userMessage: Message = {
      role: 'user',
      content: parsed ? parsed.args : raw,
      ...(parsed ? { commandName: parsed.command.name } : {}),
      attachments: currentAttachments.map(({ name, mimeType, previewUrl }) => ({ name, mimeType, previewUrl })),
    };
    const updatedMessages = [...messages, userMessage];
    stickToBottomRef.current = true;
    setMessages(updatedMessages);
    setInput('');
    setActiveCommand(null);
    streamingRef.current = true;
    setStreaming(true);
    requestAnimationFrame(focusInputIfIdle);
    setAssistantIdle(false);
    setToolCalls([]);
    setMessages([...updatedMessages, { role: 'assistant', content: '' }]);

    // Reset the typewriter reveal for this turn.
    targetTextRef.current = '';
    revealLenRef.current = 0;
    if (revealRafRef.current != null) {
      cancelAnimationFrame(revealRafRef.current);
      revealRafRef.current = null;
    }

    const previousAssistant = [...messages].reverse().find((message) => message.role === 'assistant');
    const fillTurn = parsed?.command.name === 'fill' || Boolean(previousAssistant && extractWtbClipboardText(previousAssistant.content));
    if (fillTurn) {
      fillCaptureRef.current?.reject(new Error('replaced'));
      let resolveText = (_text: string) => {};
      let rejectText = (_error: Error) => {};
      const pending = new Promise<string>((resolve, reject) => {
        resolveText = resolve;
        rejectText = reject;
      });
      armFillingClipboard(pending);
      fillCaptureRef.current = { resolve: resolveText, reject: rejectText };
    }

    try {
      abortRef.current = new AbortController();
      const response = await request(
        '/api/chat',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: apiText,
            conversationId: conversationIdRef.current,
            attachments: currentAttachments.map(({ name, mimeType, base64 }) => ({ name, mimeType, base64 })),
          }),
          signal: abortRef.current.signal,
        }
      );
      if (!response) return;

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let lineBuffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            if (event.type === 'text_delta') {
              accumulated += event.text;
              targetTextRef.current = accumulated;
              if (revealRafRef.current == null) {
                revealRafRef.current = requestAnimationFrame(pumpReveal);
              }
            } else if (event.type === 'tool_call_start') {
              setToolCalls((prev) => [...prev, { id: event.id, name: event.name, status: 'running' }]);
            } else if (event.type === 'tool_call_result') {
              const label = labelLinkOf(event.preview);
              if (label) {
                setMessages((prev) => {
                  const last = prev[prev.length - 1];
                  if (last?.role !== 'assistant') return prev;
                  return [...prev.slice(0, -1), { ...last, labels: [...(last.labels ?? []), label] }];
                });
              }
              setToolCalls((prev) => prev.map((t) => (t.id === event.id ? { ...t, status: event.isError ? 'error' : 'done' } : t)));
              setTimeout(() => {
                setToolCalls((prev) => prev.filter((t) => t.id !== event.id));
              }, 2000);
            } else if (event.type === 'error') {
              const finalText = accumulated || event.message || 'Sorry, something went wrong.';
              targetTextRef.current = finalText;
              flushReveal();
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...prev[prev.length - 1], content: finalText };
                return updated;
              });
            } else if (event.type === 'conversation' && typeof event.id === 'string') {
              const tabKey = sendingTabKeyRef.current;
              pendingTitleIdRef.current = event.id;
              if (tabKey) {
                setTabs((prev) => prev.map((tab) => (tab.key === tabKey ? { ...tab, conversationId: event.id } : tab)));
              }
              if (activeKeyRef.current === tabKey) {
                conversationIdRef.current = event.id;
                onActiveChatChangeRef.current?.(event.id);
              }
              onChatSavedRef.current?.();
            } else if (event.type === 'conversation_state') {
              onChatSavedRef.current?.();
            } else if (event.type === 'done') {
              streamingRef.current = false;
              setStreaming(false);
            } else if (event.type === 'chat_title' && typeof event.title === 'string') {
              const titledId = pendingTitleIdRef.current;
              setTabs((prev) => prev.map((tab) => (tab.conversationId === titledId ? { ...tab, title: event.title } : tab)));
              onChatSavedRef.current?.();
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) {
        const finalText = 'Sorry, something went wrong. Please try again.';
        targetTextRef.current = finalText;
        flushReveal();
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { ...prev[prev.length - 1], content: finalText };
          return updated;
        });
      } else {
        // On stop, keep whatever has already been revealed.
        flushReveal();
      }
    } finally {
      streamingRef.current = false;
      setStreaming(false);
      if (revealLenRef.current >= targetTextRef.current.length) setAssistantIdle(true);
      setToolCalls([]);
      abortRef.current = null;
      requestAnimationFrame(focusInputIfIdle);
    }
  };

  const acceptSlash = (command: SlashCommand) => {
    setActiveCommand(command);
    setInput('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (slashMenuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashHighlight((i) => (i + 1) % slashMatches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashHighlight((i) => (i - 1 + slashMatches.length) % slashMatches.length);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashDismissed(true);
        return;
      }
      if ((e.key === 'Enter' && !e.nativeEvent.isComposing) || e.key === 'Tab') {
        e.preventDefault();
        const command = slashMatches[slashHighlight] ?? slashMatches[0];
        if (command) acceptSlash(command);
        return;
      }
    }

    if (e.key === 'Backspace' && activeCommand && input === '' && (inputRef.current?.selectionStart ?? 0) === 0 && (inputRef.current?.selectionEnd ?? 0) === 0) {
      e.preventDefault();
      const name = activeCommand.name;
      setActiveCommand(null);
      setInput(`/${name}`);
      requestAnimationFrame(() => {
        const field = inputRef.current;
        const caret = `/${name}`.length;
        field?.focus();
        field?.setSelectionRange(caret, caret);
      });
      return;
    }

    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
    e.preventDefault();
    if (isMobile ? !e.shiftKey : e.shiftKey) {
      // Mobile: Enter = newline. Desktop: Shift+Enter = newline.
      const field = inputRef.current;
      const start = field?.selectionStart ?? input.length;
      const end = field?.selectionEnd ?? start;
      const caret = start + 1;
      setInput(`${input.slice(0, start)}\n${input.slice(end)}`);
      requestAnimationFrame(() => inputRef.current?.setSelectionRange(caret, caret));
      return;
    }
    handleSend();
  };

  return (
    <Box
      ref={panelRef}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Drag-and-drop overlay */}
      <Fade in={dragOver} timeout={120}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            bgcolor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            pointerEvents: 'none',
            backdropFilter: 'blur(2px)',
            borderRadius: 'inherit',
          }}
        >
          <AttachFile sx={{ fontSize: 36, color: 'white', opacity: 0.9 }} />
          <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>Drop files here</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem' }}>Images, PDFs, spreadsheets, Word docs</Typography>
        </Box>
      </Fade>

      {/* Header */}
      <Box
        onTouchStart={handleHeaderTouchStart}
        onTouchMove={handleHeaderTouchMove}
        onTouchEnd={handleHeaderTouchEnd}
        sx={{
          px: 2,
          py: 1.5,
          pt: { xs: 'calc(env(safe-area-inset-top) + 12px)', sm: 1.5 },
          bgcolor: '#000',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          flexShrink: 0,
          cursor: onClose ? { xs: 'grab', sm: 'default' } : 'default',
          transform: swipeTranslate > 0 ? `translateY(${swipeTranslate}px)` : undefined,
          transition: swipeTranslate === 0 ? 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
        }}
      >
        {/* Drag pill — mobile only, only when dismissible */}
        {onClose && (
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', mb: 1, mt: '-4px' }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.25)' }} />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
            <Image src="/timmy-icon.png" alt="Timmy" width={32} height={32} draggable={false} style={{ objectFit: 'contain', userSelect: 'none' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.3 }}>Timmy</Typography>
            <Typography sx={{ fontSize: '0.65rem', opacity: 0.5, fontWeight: 400 }}>Ask anything about policies & operations</Typography>
          </Box>
          {onOpenChats && (
            <IconButton
              size="small"
              aria-label="Chats"
              onClick={onOpenChats}
              sx={{
                color: 'rgba(255,255,255,0.5)',
                '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
                width: 28,
                height: 28,
              }}
            >
              <ForumOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {onClose && (
            <IconButton
              size="small"
              aria-label="Close"
              onClick={onClose}
              sx={{
                color: 'rgba(255,255,255,0.5)',
                '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
                width: 28,
                height: 28,
              }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {tabsReady && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            height: 36,
            bgcolor: isDark ? 'grey.900' : '#f4f4f4',
            borderBottom: '1px solid',
            borderBottomColor: 'divider',
            flexShrink: 0,
            color: 'text.primary',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              overflowX: 'auto',
              flex: 1,
              minWidth: 0,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {tabs.map((tab, index) => {
              const selected = tab.key === activeKey;
              return (
                <Box
                  key={tab.key}
                  component="button"
                  type="button"
                  onClick={() => selectTab(tab.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    maxWidth: 200,
                    minWidth: 0,
                    height: '100%',
                    flexShrink: 0,
                    m: 0,
                    p: 0,
                    px: 1.5,
                    border: 0,
                    borderRight: '1px solid',
                    borderRightColor: 'divider',
                    borderRadius: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                    appearance: 'none',
                    bgcolor: selected ? 'background.paper' : 'transparent',
                    color: selected ? 'text.primary' : 'text.secondary',
                    WebkitTextFillColor: 'currentcolor',
                    boxShadow: selected ? `inset 0 -1px 0 ${theme.palette.background.paper}` : 'none',
                    '&:hover': {
                      bgcolor: selected ? 'background.paper' : 'action.hover',
                      color: 'text.primary',
                    },
                    '&:hover .tab-close': { color: 'text.secondary' },
                    ...(index === 0 ? { borderLeft: 0 } : {}),
                  }}
                >
                  <Typography component="span" noWrap sx={{ fontSize: '0.75rem', fontWeight: selected ? 600 : 500, color: 'inherit', lineHeight: 1 }}>
                    {tab.title}
                  </Typography>
                  <Box
                    component="span"
                    className="tab-close"
                    aria-label={`Close ${tab.title}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      closeTab(tab.key);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      borderRadius: '4px',
                      color: selected ? 'text.secondary' : 'transparent',
                      '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                    }}
                  >
                    <Close sx={{ fontSize: 12 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
          <IconButton
            size="small"
            aria-label="New tab"
            onClick={openNewTab}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 0,
              color: 'text.secondary',
              borderLeft: '1px solid',
              borderLeftColor: 'divider',
              '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* Messages */}
      <Box
        ref={messagesContainerRef}
        onScroll={handleScroll}
        onWheel={markUserScroll}
        onTouchMove={markUserScroll}
        onKeyDown={markUserScroll}
        onPointerDown={() => {
          pointerHeldRef.current = true;
        }}
        onPointerUp={() => {
          pointerHeldRef.current = false;
        }}
        onPointerCancel={() => {
          pointerHeldRef.current = false;
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          px: { xs: 2, sm: 2 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          bgcolor: 'background.paper',
          position: 'relative',
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', borderRadius: 2 },
        }}
      >
        {tabsReady && !loadingTranscript && messages.length === 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              px: 'clamp(16px, 6cqw, 40px)',
              // Establish a query container so every size below can scale
              // fluidly with the panel's actual rendered box, not just the
              // window width — this keeps things proportional as the chat
              // widget itself grows/shrinks (wide desktop panel, tall mobile
              // sheet, or anything in between).
              containerType: 'size',
              containerName: 'timmy-empty',
            }}
          >
            {/* Equal flexible spacers above/below force the content to sit dead-center,
                regardless of how tall the content group ends up being. */}
            <Box sx={{ flex: 1, minHeight: 0 }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                gap: 'clamp(10px, 3.2cqh, 22px)',
                py: 'clamp(6px, 2cqh, 16px)',
              }}
            >
              {/* Icon with soft ambient shadow */}
              <Box
                sx={{
                  width: 'clamp(38px, 11cqmin, 76px)',
                  height: 'clamp(38px, 11cqmin, 76px)',
                  flexShrink: 0,
                  borderRadius: 'clamp(11px, 3.2cqmin, 22px)',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #1a1a1a, #050505)',
                  boxShadow:
                    '0 1px 0 rgba(255,255,255,0.06) inset, 0 8px 18px -6px rgba(0,0,0,0.45), 0 0 0 8px rgba(0,0,0,0.03)',
                  animation: 'timmyPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both',
                  '@keyframes timmyPop': {
                    from: { opacity: 0, transform: 'scale(0.75)' },
                    to: { opacity: 1, transform: 'none' },
                  },
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
              >
                <Image
                  src="/timmy-icon.png"
                  alt="Timmy"
                  width={44}
                  height={44}
                  draggable={false}
                  style={{ width: '58%', height: '58%', objectFit: 'contain', userSelect: 'none' }}
                />
              </Box>

              <Box>
                <Typography
                  component="h2"
                  suppressHydrationWarning
                  sx={{
                    m: 0,
                    fontSize: 'clamp(1rem, 4.4cqmin, 1.55rem)',
                    fontWeight: 700,
                    letterSpacing: '-0.03em',
                    lineHeight: 1.25,
                    color: 'text.primary',
                  }}
                >
                  {timeGreeting()}
                  {welcomeName ? `, ${welcomeName}` : ''}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'clamp(0.8rem, 3.3cqmin, 1.1rem)',
                    fontWeight: 700,
                    color: SLASH_COLOR,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                    mt: 'clamp(2px, 0.8cqh, 8px)',
                  }}
                >
                  What can I help you get done today?
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'clamp(0.68rem, 2.7cqmin, 0.92rem)',
                    color: 'text.secondary',
                    letterSpacing: '-0.005em',
                    lineHeight: 1.3,
                    mt: 'clamp(1px, 0.5cqh, 6px)',
                  }}
                >
                  Ask anything about policies &amp; operations.
                </Typography>
              </Box>

              {highlightedCommands.length > 0 && (
                <Box sx={{ width: '100%', maxWidth: 'clamp(300px, 78cqw, 480px)', mt: 'clamp(8px, 3.2cqh, 24px)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px, 1.4cqw, 12px)', mb: 'clamp(6px, 1.8cqh, 14px)' }}>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    <Typography
                      sx={{
                        fontSize: 'clamp(0.58rem, 2cqmin, 0.7rem)',
                        fontWeight: 700,
                        color: 'text.secondary',
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Or use a shortcut command
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  </Box>
                  <Box
                    component="ul"
                    sx={{
                      listStyle: 'none',
                      m: 0,
                      p: 0,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'clamp(6px, 1.6cqh, 14px)',
                      textAlign: 'left',
                    }}
                  >
                    {highlightedCommands.map((command, i) => {
                      const Icon = COMMAND_ICONS[command.name] ?? ChatBubbleOutlineRounded;
                      return (
                        <Box
                          component="li"
                          key={command.name}
                          sx={{
                            animation: 'suggestionIn 0.28s cubic-bezier(0.22, 1, 0.36, 1) both',
                            animationDelay: `${i * 40}ms`,
                            '@keyframes suggestionIn': {
                              from: { opacity: 0, transform: 'translateY(4px)' },
                              to: { opacity: 1, transform: 'none' },
                            },
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                          }}
                        >
                          <Box
                            component="button"
                            type="button"
                            onClick={() => acceptSlash(command)}
                            sx={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'clamp(8px, 2.2cqw, 16px)',
                              minWidth: 0,
                              px: 'clamp(10px, 2.8cqw, 20px)',
                              py: 'clamp(6px, 2cqh, 14px)',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 'clamp(10px, 2.8cqmin, 18px)',
                              bgcolor: isDark ? 'grey.800' : '#f7f7f8',
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              textAlign: 'left',
                              WebkitTapHighlightColor: 'transparent',
                              userSelect: 'none',
                              transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                              '@media (hover: hover)': {
                                '&:hover': {
                                  bgcolor: isDark ? 'grey.700' : '#fff',
                                  borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)',
                                  boxShadow: isDark ? 'none' : '0 3px 10px -4px rgba(0,0,0,0.1)',
                                },
                              },
                              '&:active': { transform: 'scale(0.98)' },
                              '&:focus-visible': { outline: '2px solid', outlineColor: (theme) => theme.palette.text.primary, outlineOffset: -2 },
                            }}
                          >
                            <Box
                              sx={{
                                flexShrink: 0,
                                width: 'clamp(24px, 6.2cqmin, 44px)',
                                height: 'clamp(24px, 6.2cqmin, 44px)',
                                borderRadius: 'clamp(7px, 1.8cqmin, 13px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)',
                                color: 'text.secondary',
                              }}
                            >
                              <Icon sx={{ fontSize: 'clamp(12px, 3cqmin, 21px)' }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                component="span"
                                sx={{
                                  display: 'block',
                                  fontFamily: SLASH_FONT,
                                  fontSize: 'clamp(0.72rem, 2.7cqmin, 1rem)',
                                  fontWeight: 700,
                                  color: 'text.primary',
                                  letterSpacing: '-0.02em',
                                  lineHeight: 1.25,
                                }}
                              >
                                /{command.name}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  display: 'block',
                                  fontSize: 'clamp(0.64rem, 2.3cqmin, 0.86rem)',
                                  color: 'text.secondary',
                                  lineHeight: 1.25,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {command.description}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }} />
          </Box>
        )}
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              alignItems: 'flex-start',
              gap: 0.75,
              mt: i > 0 && messages[i - 1].role !== msg.role ? 1.25 : 0.25,
            }}
          >
            {msg.role === 'assistant' && (
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  flexShrink: 0,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  bgcolor: 'black',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                  animation: 'avatarIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both',
                  '@keyframes avatarIn': {
                    from: { opacity: 0, transform: 'scale(0.4)' },
                    to: { opacity: 1, transform: 'none' },
                  },
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
              >
                <Image src="/timmy-icon.png" alt="Timmy" width={24} height={24} style={{ objectFit: 'contain' }} />
              </Box>
            )}
            <Box
              sx={{
                maxWidth: { xs: '88%', sm: '82%' },
                px: { xs: 1.6, sm: 1.5 },
                py: { xs: 1, sm: 0.875 },
                borderRadius: msg.role === 'user' ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                bgcolor: msg.role === 'user' ? (isDark ? '#e0e0e0' : '#111') : (isDark ? 'grey.800' : '#f7f7f8'),
                color: msg.role === 'user' ? (isDark ? '#111' : 'white') : 'text.primary',
                border: 'none',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                fontSize: { xs: '0.9375rem', sm: '0.84rem' },
                lineHeight: 1.5,
                boxShadow: 'none',
                // Each bubble pops in from its anchored corner as it mounts (iMessage-style).
                transformOrigin: msg.role === 'user' ? 'bottom right' : 'bottom left',
                animation: 'bubbleIn 0.34s cubic-bezier(0.34, 1.56, 0.64, 1) both',
                '@keyframes bubbleIn': {
                  from: { opacity: 0, transform: 'translateY(10px) scale(0.82)' },
                  to: { opacity: 1, transform: 'none' },
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
                ...(msg.role === 'user' && { whiteSpace: 'pre-wrap' }),
                ...(msg.role === 'assistant' && {
                  '& p': { m: 0 },
                  '& p + p': { mt: 0.75 },
                  '& ul, & ol': { m: 0, pl: 2.5, '& + p': { mt: 0.75 } },
                  '& li': { mb: 0.25, fontSize: { xs: '0.9rem', sm: '0.82rem' } },
                  '& code': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                    px: 0.5,
                    py: 0.125,
                    borderRadius: '3px',
                    fontSize: '0.76rem',
                    fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                  },
                  '& pre': {
                    bgcolor: '#111',
                    color: '#e5e5e5',
                    p: 1.5,
                    borderRadius: '6px',
                    overflow: 'auto',
                    my: 0.75,
                    fontSize: '0.76rem',
                    '& code': { bgcolor: 'transparent', p: 0, color: 'inherit' },
                  },
                  '& table': { borderCollapse: 'collapse', width: '100%', my: 0.75, fontSize: '0.76rem' },
                  '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
                  '& th': { bgcolor: isDark ? 'grey.700' : '#f5f5f5', fontWeight: 600 },
                  '& blockquote': { borderLeft: `2px solid ${theme.palette.divider}`, pl: 1.5, ml: 0, my: 0.5, color: 'text.secondary' },
                  '& h1, & h2, & h3, & h4': { mt: 1, mb: 0.5, fontSize: '0.86rem', fontWeight: 600 },
                  '& a': { color: 'text.primary', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '2px' },
                  '& hr': { border: 'none', borderTop: `1px solid ${theme.palette.divider}`, my: 0.75 },
                }),
              }}
            >
              {msg.role === 'assistant' ? (
                <AssistantMessageContent
                  content={msg.content}
                  labels={msg.labels}
                  streaming={streaming && i === messages.length - 1}
                  caretOn={caretOn}
                  showLabelActions={assistantIdle || i !== messages.length - 1}
                  onError={showError}
                />
              ) : (
                <>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: msg.content ? 0.75 : 0 }}>
                      {msg.attachments.map((att, ai) =>
                        att.previewUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img key={ai} src={att.previewUrl} alt={att.name} style={{ maxWidth: 160, maxHeight: 120, borderRadius: 6, objectFit: 'cover', display: 'block' }} />
                        ) : (
                          <Box
                            key={ai}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: 'rgba(255,255,255,0.15)',
                              borderRadius: '6px',
                              px: 0.75,
                              py: 0.25,
                              maxWidth: 160,
                            }}
                          >
                            <InsertDriveFile sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', flexShrink: 0 }} />
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                color: 'rgba(255,255,255,0.9)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {att.name}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Box>
                  )}
                  {msg.commandName && (
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        mr: 0.75,
                        px: 0.7,
                        py: '2px',
                        borderRadius: '6px',
                        bgcolor: 'rgba(255,255,255,0.92)',
                        color: SLASH_COLOR,
                        fontFamily: SLASH_FONT,
                        fontSize: { xs: '0.84rem', sm: '0.74rem' },
                        fontWeight: 600,
                        lineHeight: 1.35,
                        verticalAlign: 'baseline',
                        letterSpacing: '-0.015em',
                      }}
                    >
                      /{msg.commandName}
                    </Box>
                  )}
                  {msg.content}
                </>
              )}
            </Box>
          </Box>
        ))}
        {toolCalls.map((call) => (
          <Box
            key={call.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              ml: '38px',
              mt: 0.5,
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
            }}
          >
            {call.status === 'running' ? (
              <CircularProgress size={11} thickness={5} sx={{ color: '#aaa', flexShrink: 0 }} />
            ) : call.status === 'done' ? (
              <CheckCircle sx={{ fontSize: 13, color: '#4caf50', flexShrink: 0 }} />
            ) : (
              <ErrorOutline sx={{ fontSize: 13, color: '#f44336', flexShrink: 0 }} />
            )}
            <Typography
              sx={{
                fontSize: '0.72rem',
                color: call.status === 'running' ? 'text.secondary' : call.status === 'done' ? '#4caf50' : '#f44336',
                fontStyle: 'italic',
                transition: 'color 0.3s ease',
              }}
            >
              {toolLabel(call.name)}
              {call.status === 'running' ? '…' : ''}
            </Typography>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Scroll-to-bottom */}
      <Fade in={showScrollDown}>
        <IconButton
          onClick={() => scrollToBottom(false)}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 68,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            width: 26,
            height: 26,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ArrowDownward sx={{ fontSize: 13, color: 'text.secondary' }} />
        </IconButton>
      </Fade>

      {/* Input */}
      <Box
        sx={{
          position: 'relative',
          px: 1.5,
          pt: 1,
          pb: 'max(12px, env(safe-area-inset-bottom, 0px))',
          bgcolor: 'background.paper',
          flexShrink: 0,
          boxShadow: isDark ? '0 -1px 0 rgba(255,255,255,0.06)' : '0 -1px 0 rgba(0,0,0,0.05)',
        }}
      >
        <Fade in={slashMenuOpen} timeout={120} unmountOnExit>
          <Box
            sx={{
              position: 'absolute',
              left: 12,
              right: 12,
              bottom: '100%',
              mb: 0.5,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px',
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              zIndex: 3,
            }}
          >
            {slashMatches.map((command, i) => (
              <Box
                key={command.name}
                onMouseDown={(e) => {
                  e.preventDefault();
                  acceptSlash(command);
                }}
                onMouseEnter={() => setSlashHighlight(i)}
                sx={{
                  px: { xs: 1.5, sm: 1.25 },
                  py: { xs: 1.1, sm: 0.85 },
                  minHeight: { xs: 46, sm: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  bgcolor: i === slashHighlight ? 'rgba(0,0,0,0.05)' : 'transparent',
                }}
              >
                <Typography sx={{ fontFamily: SLASH_FONT, fontSize: { xs: '0.9rem', sm: '0.78rem' }, fontWeight: 600, color: SLASH_COLOR, flexShrink: 0 }}>/{command.name}</Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.82rem', sm: '0.7rem' },
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {command.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Fade>
        {/* Hidden file input */}
        <input ref={fileInputRef} type="file" accept={ACCEPTED_FILE_TYPES} multiple style={{ display: 'none' }} onChange={handleFileSelect} />

        {/* Attachment chips */}
        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
            {attachments.map((att, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: 'rgba(0,0,0,0.06)',
                  borderRadius: '6px',
                  pl: att.previewUrl ? 0 : 0.75,
                  pr: 0.5,
                  py: att.previewUrl ? 0 : 0.25,
                  maxWidth: 160,
                  overflow: 'hidden',
                }}
              >
                {att.previewUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={att.previewUrl} alt={att.name} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: '6px 0 0 6px', display: 'block', flexShrink: 0 }} />
                ) : (
                  <InsertDriveFile sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                )}
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    mx: 0.25,
                  }}
                >
                  {att.name}
                </Typography>
                <IconButton size="small" onClick={() => removeAttachment(i)} sx={{ p: 0, width: 16, height: 16, flexShrink: 0, '&:hover': { bgcolor: 'transparent' } }}>
                  <Close sx={{ fontSize: 11, color: 'text.secondary' }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* File error */}
        {fileError && <Typography sx={{ fontSize: '0.72rem', color: 'error.main', mb: 0.5, px: 0.25 }}>{fileError}</Typography>}

        <Box
          sx={{
            display: 'flex',
            alignItems: inputMultiRow ? 'flex-end' : 'center',
            gap: 0.75,
            bgcolor: isDark ? 'grey.800' : '#f2f2f7',
            borderRadius: '22px',
            pl: 0.5,
            pr: 0.5,
            py: { xs: '6px', sm: '5px' },
            minHeight: { xs: 44, sm: 38 },
            border: '1px solid',
            borderColor: inputFocused ? (isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)') : 'transparent',
            boxShadow: inputFocused ? (isDark ? '0 0 0 3px rgba(255,255,255,0.06)' : '0 0 0 3px rgba(0,0,0,0.04)') : 'none',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          }}
        >
          {/* Attach button */}
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={streaming}
            sx={{
              flexShrink: 0,
              color: 'text.secondary',
              width: { xs: 32, sm: 28 },
              height: { xs: 32, sm: 28 },
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)', color: 'text.primary' },
            }}
          >
            <AttachFile sx={{ fontSize: { xs: 18, sm: 16 } }} />
          </IconButton>

          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder={activeCommand ? activeCommand.hint : 'Ask a question...'}
            variant="standard"
            value={input}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setInputFocused(true);
              setTimeout(followIfStuck, 350);
            }}
            onBlur={() => setInputFocused(false)}
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiInputBase-root': {
                alignItems: 'center',
                padding: '0 !important',
                margin: 0,
                minHeight: { xs: 32, sm: 28 },
              },
              '& textarea': {
                padding: '0 !important',
                lineHeight: '22px',
                display: 'block',
              },
              '& textarea::placeholder': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            slotProps={{
              input: {
                disableUnderline: true,
                startAdornment: activeCommand ? (
                  <InputAdornment
                    position="start"
                    sx={{
                      m: 0,
                      mr: 0.75,
                      maxHeight: 'none',
                      height: 22,
                      alignSelf: inputMultiRow ? 'flex-start' : 'center',
                    }}
                  >
                    <Box
                      sx={{
                        px: 0.8,
                        height: 22,
                        display: 'inline-flex',
                        alignItems: 'center',
                        borderRadius: '6px',
                        bgcolor: 'rgba(29, 78, 216, 0.1)',
                        color: SLASH_COLOR,
                        fontFamily: SLASH_FONT,
                        fontSize: { xs: '15px', sm: '0.78rem' },
                        fontWeight: 600,
                        lineHeight: 1,
                        userSelect: 'none',
                      }}
                    >
                      /{activeCommand.name}
                    </Box>
                  </InputAdornment>
                ) : undefined,
                // Prevent iOS zoom (font-size < 16px triggers zoom)
                inputProps: {
                  autoCorrect: typingSlash || activeCommand ? 'off' : 'on',
                  autoCapitalize: typingSlash || activeCommand ? 'off' : 'sentences',
                  spellCheck: !(typingSlash || activeCommand),
                  enterKeyHint: typeof window !== 'undefined' && window.innerWidth < 600 ? 'enter' : 'send',
                },
                sx: {
                  fontSize: { xs: '16px', sm: '0.875rem' },
                  lineHeight: '22px',
                  bgcolor: 'transparent',
                  ...(typingSlash && {
                    fontFamily: SLASH_FONT,
                    color: SLASH_COLOR,
                    fontWeight: 600,
                    caretColor: SLASH_COLOR,
                    '& textarea': {
                      fontFamily: SLASH_FONT,
                      color: SLASH_COLOR,
                      fontWeight: 600,
                      caretColor: SLASH_COLOR,
                    },
                  }),
                },
              },
            }}
          />

          {/* Stop button (streaming) / Send button */}
          <Box sx={{ flexShrink: 0, position: 'relative', width: { xs: 32, sm: 28 }, height: { xs: 32, sm: 28 } }}>
            <Fade in={streaming} timeout={120} unmountOnExit>
              <IconButton
                onClick={() => abortRef.current?.abort()}
                size="small"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: '#000',
                  color: 'white',
                  borderRadius: '50%',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'transform 0.1s ease, background-color 0.12s ease',
                  '&:hover': { bgcolor: '#222' },
                  '&:active': { transform: 'scale(0.88)' },
                }}
              >
                <Stop sx={{ fontSize: { xs: 16, sm: 14 } }} />
              </IconButton>
            </Fade>
            <Fade in={!streaming} timeout={120} unmountOnExit>
              {(() => {
                const parsedDraft = activeCommand ? { command: activeCommand, args: input.trim() } : input.trim() ? parseSlashCommand(input.trim()) : null;
                const canSend = parsedDraft ? !parsedDraft.command.requiresArgs || !!parsedDraft.args : !!(input.trim() || attachments.length > 0);
                return (
                  <IconButton
                    onClick={() => handleSend()}
                    disabled={!canSend}
                    size="small"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: canSend ? '#000' : 'rgba(0,0,0,0.12)',
                      color: canSend ? 'white' : 'rgba(255,255,255,0.85)',
                      borderRadius: '50%',
                      WebkitTapHighlightColor: 'transparent',
                      transition: 'transform 0.1s ease, background-color 0.12s ease',
                      '&:hover': { bgcolor: canSend ? '#1a1a1a' : 'rgba(0,0,0,0.12)' },
                      '&:active': { transform: canSend ? 'scale(0.88)' : 'none' },
                      '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.12)', color: 'rgba(255,255,255,0.85)' },
                    }}
                  >
                    <ArrowUpward sx={{ fontSize: { xs: 18, sm: 16 } }} />
                  </IconButton>
                );
              })()}
            </Fade>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
